import { useEffect, useState, useCallback, useRef } from "react";
import { Building2, ArrowLeft, Upload, Download, Trash2, FileText, Loader2, CheckCircle2, AlertTriangle, Sparkles, Eye } from "lucide-react";
import { api, useAuth, formatApiError, downloadFile, canDo } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import StatutBadge from "../components/StatutBadge";
import { toast } from "sonner";

function AnalyseBlock({ cert, emp, onApplied, canWrite }) {
  const a = cert.analyse;
  const [applying, setApplying] = useState(false);
  if (!a) return null;
  if (a.statut === "en_cours") {
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground px-3 py-2" data-testid={`cert-analysis-${cert.id}`}>
        <Loader2 className="h-3.5 w-3.5 animate-spin text-accent" /> Analyse automatique du certificat en cours...
      </p>
    );
  }
  if (a.statut === "erreur") {
    return (
      <p className="flex items-center gap-2 text-xs text-amber-400 px-3 py-2" data-testid={`cert-analysis-${cert.id}`}>
        <AlertTriangle className="h-3.5 w-3.5" /> {a.message}
      </p>
    );
  }
  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post(`/certificats/${cert.id}/appliquer`);
      toast.success(`Fiche mise à jour : formation ${data.updates.date_formation || "inchangée"}, validité ${data.updates.fin_validite || "inchangée"}`);
      onApplied();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };
  return (
    <div className="mx-3 mb-2 rounded-lg border border-emerald-800/40 bg-emerald-950/10 p-3 space-y-2" data-testid={`cert-analysis-${cert.id}`}>
      <p className="flex items-center gap-1.5 text-xs font-semibold text-emerald-500">
        <Sparkles className="h-3.5 w-3.5" /> Analyse automatique (confiance : {a.confiance}{a.lisible === false ? " · document partiellement lisible" : ""})
      </p>
      <p className="text-xs">
        Détecté : <span className="font-medium">{a.prenom || "?"} {a.nom || "?"}</span>
        {a.date_formation && <> · formation <span className="font-mono">{a.date_formation}</span></>}
        {a.date_validite && <> · validité <span className="font-mono">{a.date_validite}</span></>}
        {!a.date_formation && !a.date_validite && <> · aucune date détectée</>}
      </p>
      {!a.correspondance && (
        <p className="flex items-center gap-1.5 text-xs text-red-400" data-testid={`cert-mismatch-${cert.id}`}>
          <AlertTriangle className="h-3.5 w-3.5" /> Le nom détecté ne correspond pas à {emp.prenom} {emp.nom} — vérifiez avant d'appliquer.
        </p>
      )}
      {a.appliquee ? (
        <p className="flex items-center gap-1.5 text-xs text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> Informations appliquées à la fiche du salarié</p>
      ) : canWrite && (a.date_formation || a.date_validite) && (
        <Button size="sm" variant="outline" onClick={apply} disabled={applying} data-testid={`apply-analysis-${cert.id}`}>
          {applying ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
          Appliquer à la fiche
        </Button>
      )}
    </div>
  );
}

export default function Certificats() {
  const { user } = useAuth();
  const readOnly = !canDo(user, "sst", "modifier");
  const [services, setServices] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);
  const targetEmp = useRef(null);

  const openPreview = async (c) => {
    try {
      const r = await api.get(`/certificats/${c.id}/download`, { responseType: "blob" });
      setPreview({ url: URL.createObjectURL(r.data), filename: c.filename });
    } catch (e) {
      toast.error(formatApiError(e, "Aperçu impossible"));
    }
  };

  const closePreview = () => {
    if (preview) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const loadServices = useCallback(() => {
    api.get("/services").then((r) => setServices(r.data.services)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);

  const loadService = useCallback((nom, silent = false) => {
    if (!silent) setLoading(true);
    api.get(`/services/${encodeURIComponent(nom)}/certificats`)
      .then((r) => setDetail(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadServices(); }, [loadServices]);

  useEffect(() => {
    if (!detail) return;
    const pending = detail.employees.some((e) => e.certificats.some((c) => c.analyse?.statut === "en_cours"));
    if (!pending) return;
    const t = setTimeout(() => loadService(detail.service, true), 3000);
    return () => clearTimeout(t);
  }, [detail, loadService]);

  const pickFile = (emp) => {
    targetEmp.current = emp;
    fileRef.current?.click();
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    const emp = targetEmp.current;
    if (!file || !emp) return;
    setUploadingFor(emp.id);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/employees/${emp.id}/certificats`, form);
      toast.success(`Certificat téléversé pour ${emp.prenom} ${emp.nom} — analyse en cours`);
      loadService(detail.service, true);
    } catch (err) {
      toast.error(formatApiError(err, "Échec du téléversement"));
    } finally {
      setUploadingFor(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = (c) => downloadFile(`/certificats/${c.id}/download`, c.filename).catch((e) => toast.error(formatApiError(e, "Téléchargement impossible")));

  const remove = async (c) => {
    if (!window.confirm(`Supprimer le certificat "${c.filename}" ?`)) return;
    try {
      await api.delete(`/certificats/${c.id}`);
      toast.success("Certificat supprimé");
      loadService(detail.service, true);
      loadServices();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (detail) {
    return (
      <div className="space-y-6" data-testid="certificats-service-page">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={() => setDetail(null)} data-testid="back-to-services">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Services
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>Service {detail.service}</h1>
            <p className="text-sm text-muted-foreground">{detail.employees.length} salarié(s)</p>
          </div>
        </div>
        <input ref={fileRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={upload} data-testid="certificat-file-input" />
        <div className="space-y-3">
          {detail.employees.map((emp) => (
            <div key={emp.id} className="rounded-xl border border-border bg-card" data-testid={`cert-employee-${emp.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="flex items-center gap-3 min-w-0">
                  <p className="font-medium">{emp.prenom} {emp.nom}</p>
                  <StatutBadge statut={emp.statut} joursRestants={emp.jours_restants} />
                </div>
                {!readOnly && (
                  <Button size="sm" variant="outline" disabled={uploadingFor === emp.id} onClick={() => pickFile(emp)} data-testid={`cert-upload-${emp.id}`}>
                    {uploadingFor === emp.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
                    Téléverser un certificat
                  </Button>
                )}
              </div>
              {emp.certificats.length === 0 && (
                <p className="px-4 pb-3 text-xs text-muted-foreground">Aucun certificat enregistré.</p>
              )}
              {emp.certificats.map((c) => (
                <div key={c.id} className="border-t border-border/50" data-testid={`cert-item-${c.id}`}>
                  <div className="flex items-center justify-between gap-2 px-4 py-2">
                    <button onClick={() => openPreview(c)} className="flex items-center gap-2 text-sm min-w-0 text-left hover:text-emerald-400 transition-colors" data-testid={`cert-preview-${c.id}`}>
                      <FileText className="h-4 w-4 text-emerald-500 shrink-0" />
                      <span className="truncate underline-offset-2 hover:underline">{c.filename}</span>
                      <span className="text-xs text-muted-foreground font-mono shrink-0">{c.created_at?.slice(0, 10)}</span>
                    </button>
                    <div className="flex items-center shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openPreview(c)} data-testid={`cert-view-${c.id}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => download(c)} data-testid={`cert-download-${c.id}`}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                      {!readOnly && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-red-400" onClick={() => remove(c)} data-testid={`cert-delete-${c.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <AnalyseBlock cert={c} emp={emp} canWrite={!readOnly} onApplied={() => { loadService(detail.service, true); loadServices(); }} />
                </div>
              ))}
            </div>
          ))}
        </div>
        <Dialog open={!!preview} onOpenChange={(o) => !o && closePreview()}>
          <DialogContent className="max-w-4xl" data-testid="certificat-preview-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-emerald-500" /> {preview?.filename}</DialogTitle>
              <DialogDescription>Aperçu du certificat PDF</DialogDescription>
            </DialogHeader>
            {preview && <iframe src={preview.url} title={preview.filename} className="w-full h-[70vh] rounded-lg border border-border bg-white" data-testid="certificat-preview-frame" />}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="certificats-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-emerald-500 mb-1">Base de données par service</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit" }}>Certificats <span className="text-emerald-500">SST</span></h1>
        <p className="text-sm text-muted-foreground mt-1">Cliquez sur un service pour consulter et gérer les certificats PDF de ses salariés. Chaque PDF téléversé est analysé automatiquement.</p>
      </div>
      {loading && <p className="text-sm text-muted-foreground">Chargement...</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {services.map((s) => (
          <button key={s.id} onClick={() => loadService(s.nom)} data-testid={`service-card-${s.id}`}
            className="rounded-xl border border-border bg-card p-5 text-left transition-all hover:-translate-y-0.5 hover:border-emerald-500">
            <div className="flex items-center justify-between mb-3">
              <Building2 className="h-5 w-5 text-emerald-500" />
              <span className="text-xs font-mono text-muted-foreground">{s.nb_certificats} PDF</span>
            </div>
            <p className="font-bold">{s.nom}</p>
            <p className="text-xs text-muted-foreground mt-1 font-mono">{s.nb_sst} SST / {s.nb_salaries} salariés</p>
          </button>
        ))}
      </div>
    </div>
  );
}
